import React, { useMemo } from 'react';
import { RoomPuzzleInfo, RoomStageInfo } from '../../common/types';

export type RoomStageDetailedProgressProps = {
  stages: RoomStageInfo[];
  solvedPuzzle?: string;
  completedStage?: string;
};

export function RoomStageDetailedProgress(props: RoomStageDetailedProgressProps) {
  const { stages } = props;
  const completedStage = useMemo(() => {
    return stages.find((stage) => stage.stageUuid === props.completedStage);
  }, [stages, props.completedStage]);

  const solvedPuzzle = useMemo(() => {
    return stages
      .flatMap((stage) => stage.puzzles as RoomPuzzleInfo[])
      .find((puzzle) => puzzle.puzzleUuid === props.solvedPuzzle);
  }, [stages, props.solvedPuzzle]);

  const refValue = completedStage?.completedAt || solvedPuzzle?.completedAt;
  const referenceDate = refValue ? new Date(refValue) : null;

  return (
    <div className="mb-2">
      {/* Natural wrapping of text, no forced wrapping of stages */}
      <div className="flex justify-between items-end flex-wrap overflow-hidden">
        {stages.map((stage, index) => {
          // Determine the ball color based on stage progress
          let ballColor = 'bg-gray-500'; // Default: not started
          if (stage.completedAt && (!referenceDate || stage.completedAt <= referenceDate)) {
            ballColor = 'bg-blue-700'; // Completed
          } else if (
            stage.startedAt &&
            (!referenceDate ||
              stage.startedAt <= referenceDate ||
              (completedStage && stage.stageUuid === completedStage.nextStageUuid))
          ) {
            ballColor = 'bg-blue-500'; // Started
          }

          return (
            // change this
            <div
              key={stage.stageUuid}
              className="flex flex-col items-center justify-end h-full w-24 max-w-24 sm:w-32 sm:max-w-32 mb-4"
            >
              {/* Stage node with hover tooltip for the stage name */}
              <div className="text-center mb-2">
                <h3 className="text-white text-sm px-2 break-words">{stage.name}</h3>
              </div>

              <div
                className={`rounded-full flex items-center justify-center text-white font-bold w-12 h-12 ${ballColor}`}
                title={`Stage #${stage.number} ${stage.name} ${
                  stage.completedAt && (!referenceDate || stage.completedAt <= referenceDate)
                    ? '- Completed'
                    : stage.startedAt && (!referenceDate || stage.startedAt <= referenceDate)
                    ? '- In Progress'
                    : '- Not Started'
                }`} // Stage name on hover
              >
                <span className="text-lg">{stage.number}</span>
              </div>

              {/* Visual representation of puzzles */}
              <div className="flex space-x-1 mt-2">
                {/* show color based on completion */}
                {stage.puzzles.map((puzzle, i) => (
                  <div
                    key={puzzle.puzzleUuid}
                    className={`w-3 h-3 rounded-full ${
                      puzzle.completedAt && (!referenceDate || puzzle.completedAt <= referenceDate)
                        ? puzzle.puzzleUuid === props.solvedPuzzle
                          ? 'bg-teal-300'
                          : 'bg-blue-300'
                        : 'bg-gray-600'
                    }`}
                    title={`Puzzle #${stage.number}.${puzzle.number} ${
                      puzzle.puzzleUuid === props.solvedPuzzle ? puzzle.name : ''
                    } ${puzzle.completedAt ? '- Solved' : '- Unsolved'}`} // Stage name on hover
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
